import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('oms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Only ever destroy the session when the SERVER says the token itself is bad (code: AUTH_INVALID).
// A network drop, a 5xx, or a database timeout must not log anyone out — that was the cause of the
// "random logout" reports: any failure anywhere looked identical to a stolen token.
export const isAuthFailure = (err) =>
  err?.response?.status === 401 && err?.response?.data?.code === 'AUTH_INVALID';

// A global write freeze (server middleware/maintenance.js). Reads keep working, so the panel
// still renders — only writes come back 503 with this code. Deliberately NOT routed through the
// logout path: the session is perfectly valid, the system is just briefly read-only.
export const isMaintenance = (err) =>
  err?.response?.status === 503 && err?.response?.data?.code === 'MAINTENANCE';

// Tiny pub/sub so a banner can react without every caller having to handle it. The interceptor
// broadcasts once when a freeze is first seen, and again when a write succeeds after one.
const maintenanceSubs = new Set();
let maintenanceState = { active: false, message: '', started_at: null };

export const onMaintenance = (cb) => {
  maintenanceSubs.add(cb);
  cb(maintenanceState);                       // fire immediately with current state
  return () => maintenanceSubs.delete(cb);
};

const setMaintenanceState = (next) => {
  if (next.active === maintenanceState.active && next.message === maintenanceState.message) return;
  maintenanceState = next;
  maintenanceSubs.forEach((cb) => { try { cb(next); } catch { /* a bad subscriber must not break others */ } });
};

// Send the user to sign in, remembering where they were so they land back there instead of at the
// dashboard. Being kicked out mid-task shouldn't also lose your place.
const redirectToLogin = (extraQuery = '') => {
  const here = window.location.pathname + window.location.search;
  const keep = !here.startsWith('/user') && here !== '/';
  const parts = [extraQuery, keep ? `next=${encodeURIComponent(here)}` : ''].filter(Boolean);
  window.location.href = `/user${parts.length ? `?${parts.join('&')}` : ''}`;
};

const isRetriable = (err) => {
  if (!err.response) return true;                 // network error / timeout — no response at all
  const s = err.response.status;
  return s === 503 || s === 502 || s === 504 || s === 429;
};

api.interceptors.response.use(
  (res) => {
    // A write that succeeds proves the freeze has lifted — clear the banner without polling.
    if ((res.config?.method || 'get').toLowerCase() !== 'get' && maintenanceState.active) {
      setMaintenanceState({ active: false, message: '', started_at: null });
    }
    return res;
  },
  async (err) => {
    // Surface the freeze before the retry branch: a maintenance 503 is a deliberate answer,
    // not an infrastructure blip, and retrying it would just burn a second request.
    if (isMaintenance(err)) {
      setMaintenanceState({
        active: true,
        message: err.response.data.error || 'We are upgrading our systems.',
        started_at: err.response.data.started_at || null,
      });
      return Promise.reject(err);
    }

    // One quiet retry for read-only requests that failed for infrastructure reasons.
    const cfg = err.config;
    if (cfg && !cfg.__retried && (cfg.method || 'get').toLowerCase() === 'get' && isRetriable(err)) {
      cfg.__retried = true;
      await new Promise(r => setTimeout(r, 600));
      return api(cfg);
    }

    if (isAuthFailure(err)) {
      localStorage.removeItem('oms_token');
      localStorage.removeItem('oms_dl_token');
      redirectToLogin();
    }
    if (err.response?.status === 403 && err.response?.data?.disabled) {
      localStorage.removeItem('oms_token');
      localStorage.removeItem('oms_dl_token');
      redirectToLogin('disabled=1');
    }
    return Promise.reject(err);
  }
);

const adminApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('oms_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('oms_admin_token');
      window.location.href = '/admnsd';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  registerInit: (data) => api.post('/auth/register-init', data),
  registerVerify: (data) => api.post('/auth/register-verify', data),
  registerResend: (data) => api.post('/auth/register-resend', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  getDownloadToken: () => api.get('/auth/dl-token'),
  welcomeSeen: () => api.post('/auth/welcome-seen'),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  requestPasswordChange: (current_password, new_password) => api.post('/auth/change-password/request', { current_password, new_password }),
  verifyPasswordChange: (otp) => api.post('/auth/change-password/verify', { otp }),
  forceChangePassword: (data) => api.post('/auth/force-change-password', data),
  demo: () => api.post('/auth/demo'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  onboarding: (data) => api.post('/auth/onboarding', data),
};

export const adminAPI = {
  login: (data) => adminApi.post('/admin/login', data),
  getUsers: () => adminApi.get('/admin/users'),
  getUser: (id) => adminApi.get(`/admin/users/${id}`),
  updateUser: (id, data) => adminApi.patch(`/admin/users/${id}`, data),
  approveUser: (id) => adminApi.post(`/admin/users/${id}/approve`),
  disableUser: (id) => adminApi.post(`/admin/users/${id}/disable`),
  enableUser: (id) => adminApi.post(`/admin/users/${id}/enable`),
  resetPassword: (id, temp_password) => adminApi.post(`/admin/users/${id}/reset-password`, { temp_password }),
  getBilling: () => adminApi.get('/admin/billing'),
  setBilling: (payload) => adminApi.put('/admin/billing', typeof payload === 'object' ? payload : { per_order_rate: payload }),
  getPaymentConfig: () => adminApi.get('/admin/payment-config'),
  setPaymentConfig: (data) => adminApi.put('/admin/payment-config', data),
  addCredit: (id, amount, note) => adminApi.post(`/admin/users/${id}/credit`, { amount, note }),
  setUserRate: (id, rate) => adminApi.post(`/admin/users/${id}/rate`, { rate }),
  getUserRateCard: (id) => adminApi.get(`/admin/users/${id}/rate-card`),
  setUserRateCard: (id, rates) => adminApi.put(`/admin/users/${id}/rate-card`, { rates }),
  adjustCredit: (id, amount, note) => adminApi.post(`/admin/users/${id}/adjust`, { amount, note }),
  getUserWallet: (id) => adminApi.get(`/admin/users/${id}/wallet`),
  bulkCredit: (amount, note, status) => adminApi.post('/admin/bulk-credit', { amount, note, status }),
  dashboard: () => adminApi.get('/admin/dashboard'),
  invoices: () => adminApi.get('/admin/invoices'),
  payments: () => adminApi.get('/admin/payments'),
  ledger: (params) => adminApi.get('/admin/ledger', { params }),
  audit: () => adminApi.get('/admin/audit'),
  gstReport: () => adminApi.get('/admin/reports/gst'),
  gstReportCsv: () => adminApi.get('/admin/reports/gst', { params: { format: 'csv' }, responseType: 'blob' }),
  // A81 - invoice-wise GST detail for the CA. `month` optional; omit for every invoice.
  gstInvoicesCsv: (month) => adminApi.get('/admin/reports/gst/invoices', { params: { format: 'csv', ...(month ? { month } : {}) }, responseType: 'blob' }),
  getLeads: () => adminApi.get('/admin/leads'),
  updateLead: (id, data) => adminApi.patch(`/admin/leads/${id}`, data),
  getTrackingConfig: () => adminApi.get('/admin/tracking-config'),
  setTrackingConfig: (data) => adminApi.put('/admin/tracking-config', data),
  changePassword: (current_password, new_password) => adminApi.put('/admin/change-password', { current_password, new_password }),
  getNotifyEmails: () => adminApi.get('/admin/notify-emails'),
  setNotifyEmails: (emails) => adminApi.put('/admin/notify-emails', { emails }),
  // A88 — manual notices on the seller System Status page
  serverVitals: () => adminApi.get('/admin/server-vitals'),   // A89 — live memory/CPU/disk for the admin Status section
  statusNotices: () => adminApi.get('/admin/status-notices'),
  createStatusNotice: (data) => adminApi.post('/admin/status-notices', data),
  updateStatusNotice: (id, data) => adminApi.patch(`/admin/status-notices/${id}`, data),
};

// Public (no auth) — Book a Demo lead capture.
export const publicAPI = {
  demoLead: (data) => axios.post(`${process.env.REACT_APP_API_URL || '/api'}/public/demo-lead`, data),
};

export const storesAPI = {
  getAll: () => api.get('/stores'),
  add: (data) => api.post('/stores', data),
  toggle: (id) => api.patch(`/stores/${id}/toggle`),
  toggleStockConfirmation: (id) => api.patch(`/stores/${id}/stock-confirmation`),
  toggleAutoPO: (id) => api.patch(`/stores/${id}/auto-po`),
  updateBranding: (id, data) => api.patch(`/stores/${id}/branding`, data),
  // A98 Branded Tracking — per-store page address + switches
  checkTrackSlug: (slug, storeId) => api.get('/stores/track-slug/check', { params: { slug, store: storeId } }),
  updateBrandedTracking: (id, data) => api.patch(`/stores/${id}/branded-tracking`, data),
  trackProducts: (id, q = '') => api.get(`/stores/${id}/track-products`, { params: { q } }),
  rename: (id, store_name) => api.patch(`/stores/${id}/name`, { store_name }),
  sync: (id, force = false) => api.post(`/stores/${id}/sync`, { force }),
  syncAll: () => api.post('/stores/sync-all', {}, { timeout: 120000 }), // full sync can take >30s
  importFrom: (id, from) => api.post(`/stores/${id}/import-from`, { from }, { timeout: 120000 }),
  importProducts: (id) => api.post(`/stores/${id}/import-products`),
};

export const shopifyAPI = {
  // Links an App Store pending install (created via /api/shopify/begin) to the logged-in user
  claimInstall: (pending_install) => api.post('/shopify/claim', { pending_install }),
  // Start per-store OAuth with the merchant's own app credentials; returns { authUrl }
  oauthInit: (data) => api.post('/shopify/oauth-init', data),
  // Re-authorize an existing store with its stored credentials; returns { authUrl }
  oauthReinit: (storeId) => api.post('/shopify/oauth-reinit', { storeId }),
};

export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  search: (q) => api.get('/orders/search', { params: { q } }),
  getCounts: (params) => api.get('/orders/counts', { params }),
  getOne: (id) => api.get(`/orders/${id}`),
  getMoney: (id) => api.get(`/orders/${id}/money`),
  getStats: (params) => api.get('/orders/stats', { params }),
  getAnalytics: (params) => api.get('/orders/analytics', { params }),
  getProductOptions: (params) => api.get('/orders/product-options', { params }),
  getRevenue: (params) => api.get('/orders/revenue', { params }),
  getCourierAnalysis: (params) => api.get('/orders/courier-analysis', { params }),
  getModiq: (range) => api.get('/orders/modiq', { params: range && range !== 'today' ? { range } : {} }),
  getModiqProducts: (params) => api.get('/orders/modiq/products', { params }),
  getRemittance: (params) => api.get('/orders/remittance', { params }),
  updateStatus: (id, courier_status, skipLog = false) => api.patch(`/orders/${id}/status`, { courier_status, skipLog }),
  getDimensions: (id) => api.get(`/orders/${id}/dimensions`),
  updateDimensions: (id, data) => api.patch(`/orders/${id}/dimensions`, data),
  // Long-running ops — override the default 30s axios timeout (server allows up to 120s)
  // A83 - send the ids of the orders on screen; the server refuses an empty list. Account-wide
  // sweeps are the cron's job, not a button's.
  syncTracking: (orderIds) => api.post('/orders/sync-tracking', { order_ids: orderIds }, { timeout: 120000 }),
  split: (id, itemIndices) => api.post(`/orders/${id}/split`, { itemIndices }),
  restoreSplit: (id) => api.post(`/orders/${id}/restore-split`),
  fixOrder: (id, data) => api.post(`/orders/${id}/fix`, data),
  // A62: the customer behind this order — profile + their order history.
  orderCustomer: (id) => api.get(`/orders/${id}/customer`),
  // A78: CSV export. Creating one returns a job id; the file is fetched via a freshly signed URL,
  // because export files outlive the 7-day maximum lifetime of a signed link.
  createExport: (body) => api.post('/orders/export', body),
  listExports: () => api.get('/orders/exports'),
  exportDownloadUrl: (id) => api.get(`/orders/exports/${id}/download`),
  cloneOrder: (id) => api.post(`/orders/${id}/clone`),
  confirmOrder: (id) => api.post(`/orders/${id}/confirm`),
  overrideStatus: (id, courier_status) => api.post(`/orders/${id}/override-status`, { courier_status }),
  labelUrl: (id) => `${process.env.REACT_APP_API_URL || '/api'}/orders/${id}/label?token=${localStorage.getItem('oms_dl_token')}`,
  invoiceUrl: (id) => `${process.env.REACT_APP_API_URL || '/api'}/orders/${id}/invoice?token=${localStorage.getItem('oms_dl_token')}`,
  // A real <form> POST, not fetch+blob — a GET with 200+ ids blew past the URL length limit
  // ("Error: URI Too Long") once large batches became possible. A fetch-then-window.open() fix
  // opened a blank tab instead in some browsers (the async gap between the click and the navigation
  // made it look like a new, script-initiated popup rather than a direct response to the click, and
  // browsers vary on how they handle that). A form submit is a genuine synchronous browser
  // navigation — same reliability as the original GET, just with ids in the POST body instead of
  // the URL. The auth token stays in the action URL's query string exactly like the single-order
  // label endpoints already do (small, already allowlisted server-side — it was never the token
  // that made the URL too long, it was 200+ ids).
  // format=pdf — a real server-rendered PDF (existing headless-Chromium path, previously unused by
  // this flow) instead of a live HTML page that auto-triggers window.print(). The live-HTML approach
  // crashed the browser tab past ~89 orders: the browser had to rasterize a print-preview for every
  // page of an image-heavy document all at once. A real PDF, opened in the browser's native PDF
  // viewer, doesn't hit that path — this is the interim fix while the server-side bulk-job version
  // (A49 in ACTION_ITEMS.md) is designed.
  openBulkLabels: (ids) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${process.env.REACT_APP_API_URL || '/api'}/orders/bulk-label?token=${localStorage.getItem('oms_dl_token')}&format=pdf`;
    form.target = '_blank';
    ids.forEach(id => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'ids';
      input.value = id;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  },
};

export const couriersAPI = {
  getAll: () => api.get('/couriers'),
  connect: (data) => api.post('/couriers/connect', data),
  updateMeta: (id, data) => api.patch(`/couriers/${id}/meta`, data),
  health: (id) => api.post(`/couriers/account/${id}/health`),
  updateCredentials: (id, credentials) => api.patch(`/couriers/account/${id}/credentials`, { credentials }),
  restore: (id) => api.post(`/couriers/account/${id}/restore`),
  removePermanent: (id) => api.delete(`/couriers/account/${id}/permanent`),
  disconnect: (id) => api.delete(`/couriers/account/${id}`),
  regenerateVelocityKey: () => api.post('/couriers/velocity/regenerate-key'),
  ship: (data) => api.post('/couriers/ship', data),
  // A97: each order's return address from its products (Return Address by Product add-on).
  returnPreview: (orderIds) => api.post('/couriers/return-preview', { orderIds }),
  // A61: live sub-courier rates for ONE order on an aggregator. Books nothing.
  rates: (params) => api.get('/couriers/rates', { params }),
  track: (data) => api.post('/couriers/track', data),
  cancelShipment: (orderId) => api.post('/couriers/cancel-shipment', { orderId }),
  cancelOrder: (orderId) => api.post('/couriers/cancel-order', { orderId }),
  markRTO: (orderId) => api.post('/couriers/mark-rto', { orderId }),
  markReceived: (orderId) => api.post('/couriers/mark-received', { orderId }),
  manualUpdateTracking: (data) => api.post('/couriers/manual/update-tracking', data),
  manualPushStatus: (orderId, status) => api.post('/couriers/manual/push-status', { orderId, status }),
};

// Public (no-auth) tracking page fetch.
export const publicTracking = (vid) => api.get(`/public/tracking/${vid}`);

export const productsAPI = {
  getAll: () => api.get('/products'),
  update: (id, data) => api.patch(`/products/${id}`, data),
  bulkUpdate: (rows) => api.post('/products/bulk-update', { rows }),
  applyDefaultWeight: () => api.post('/products/apply-default-weight'),
  transferStock: (id, data) => api.post(`/products/${id}/transfer-stock`, data),
  adjustStock: (data) => api.post('/products/adjust-stock', data),
  previewMasterLink: (groups) => api.post('/products/master/preview', { groups }),
  linkMaster: (groups) => api.post('/products/master/link', { groups }),
  countMasterStock: (rows) => api.post('/products/master/count', { rows }),
  unlinkMasterListing: (masterId, memberId, returns) => api.post(`/products/${masterId}/master/unlink`, { member_id: memberId, returns }),
  getLogs: (id) => api.get(`/products/${id}/logs`),
  getStatement: (id) => api.get(`/products/${id}/statement`),
  statementPdfUrl: (id) => `${process.env.REACT_APP_API_URL || '/api'}/products/${id}/statement/pdf?format=pdf&token=${localStorage.getItem('oms_dl_token')}`,
};

// A33 — Amazon channel (beta). Every call 403s unless the admin switched Amazon on for the account.
export const amazonAPI = {
  getSkuMap: () => api.get('/amazon/sku-map'),
  uploadListings: (rows) => api.post('/amazon/sku-map/upload', { rows }),
  rematch: () => api.post('/amazon/sku-map/rematch'),
  setLink: (id, data) => api.patch(`/amazon/sku-map/${id}`, data),
  connectInit: () => api.post('/amazon/connect/init'),
  connectContinue: (data) => api.post('/amazon/connect/continue', data),
};

export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  refresh: () => api.post('/customers/refresh'),
};

export const creditNotesAPI = {
  getAll: () => api.get('/credit-notes'),
  update: (id, data) => api.patch(`/credit-notes/${id}`, data),
  confirm: (id) => api.post(`/credit-notes/${id}/confirm`),
  cancel: (id) => api.delete(`/credit-notes/${id}`),
};

export const vendorsAPI = {
  getAll: () => api.get('/vendors'),
  add: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

export const purchaseOrdersAPI = {
  getPlanning: () => api.get('/purchase-orders/planning'),
  getPlanningAnalysis: (productId) => api.get(`/purchase-orders/planning/${productId}/analysis`),
  getStock: () => api.get('/purchase-orders/stock'),
  getBuyingPrices: () => api.get('/purchase-orders/buying-prices'),
  getAll: (params) => api.get('/purchase-orders', { params }),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.patch(`/purchase-orders/${id}`, data),
  confirmInbound: (id, data) => api.post(`/purchase-orders/${id}/confirm-inbound`, data),
  confirmDraft: (id, data) => api.post(`/purchase-orders/${id}/confirm-draft`, data),
  cancel: (id) => api.delete(`/purchase-orders/${id}`),
  pdfUrl: (id) => `${process.env.REACT_APP_API_URL || '/api'}/purchase-orders/${id}/pdf?token=${localStorage.getItem('oms_dl_token')}`,
};

export const activityLogsAPI = {
  getAll: () => api.get('/activity-logs'),
  log: (data) => api.post('/activity-logs', data),
};

export const vendorPaymentsAPI = {
  create: (data) => api.post('/vendor-payments', data),
  adjust: (data) => api.post('/vendor-payments/adjust', data),
  getAll: (params) => api.get('/vendor-payments', { params }),
  getDues: (params) => api.get('/vendor-payments/dues', { params }),
  getLedger: (vendor_id, params) => api.get(`/vendor-payments/ledger/${vendor_id}`, { params }),
};

export const pickupsAPI = {
  generate: (order_ids) => api.post('/pickups/generate', { order_ids }),
  getAll: (params) => api.get('/pickups', { params }),
  getPacker: (id) => api.get(`/pickups/${id}/packer`),
  packerCombined: (pickup_ids) => api.post('/pickups/packer-combined', { pickup_ids }),
};

export const shippingCostsAPI = {
  getAll: (params) => api.get('/shipping-costs', { params }),
  estimate: (data) => api.post('/shipping-costs/estimate', data),
};

// A96 - Courier Cost Reconciliation (upload a courier bill, match AWBs, Estimated -> Billed).
export const costReconcileAPI = {
  config: () => api.get('/cost-reconcile/config'),
  summary: (params) => api.get('/cost-reconcile/summary', { params }),
  unbilled: (params) => api.get('/cost-reconcile/unbilled', { params }),
  batches: () => api.get('/cost-reconcile/batches'),
  createBatch: (data) => api.post('/cost-reconcile/batches', data),
  appendLines: (id, offset, lines) => api.post(`/cost-reconcile/batches/${id}/lines`, { offset, lines }, { timeout: 60000 }),
  // Matching / confirming a 20,000+ line bill can take minutes — the default 30 s would cut it off.
  analyse: (id) => api.post(`/cost-reconcile/batches/${id}/analyse`, {}, { timeout: 300000 }),
  batch: (id) => api.get(`/cost-reconcile/batches/${id}`),
  lines: (id, params) => api.get(`/cost-reconcile/batches/${id}/lines`, { params }),
  exportLines: (id) => api.get(`/cost-reconcile/batches/${id}/export`, { timeout: 120000 }),
  confirm: (id, actions) => api.post(`/cost-reconcile/batches/${id}/confirm`, { actions }, { timeout: 300000 }),
  cancel: (id) => api.post(`/cost-reconcile/batches/${id}/cancel`),
  edit: (id, data) => api.patch(`/cost-reconcile/batches/${id}`, data),
  revert: (id, reason) => api.post(`/cost-reconcile/batches/${id}/revert`, { reason }, { timeout: 300000 }),
  parseInvoicePdf: (file) => api.post('/cost-reconcile/invoice-pdf/parse', file, { headers: { 'Content-Type': 'application/octet-stream' } }),
  uploadFile: (id, kind, file) => api.post(`/cost-reconcile/batches/${id}/files/${kind}`, file, { params: { name: file.name }, headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 }),
  fileLink: (id, kind) => api.get(`/cost-reconcile/batches/${id}/files/${kind}`),
};

// A83 - preferences that belong to the PERSON signed in, not the account. Mounted apart from
// /settings on purpose: every write there needs the `setup` module, and a view-only packer must
// still be able to choose their own columns.
export const prefsAPI = {
  setOrdersView: (selected) => api.put('/prefs/orders-view-selected', { selected }),
};

// v1.247.0 — ModIQ › Profit & Loss
export const profitAPI = {
  get: (params) => api.get('/profit', { params }),
  stuck: () => api.get('/profit/stuck'),
  saveInput: (day, data) => api.put(`/profit/inputs/${day}`, data),
  getSettings: () => api.get('/profit/settings'),
  saveSettings: (data) => api.put('/profit/settings', data),
  getMonthly: (month) => api.get('/profit/monthly/' + month),
  saveMonthly: (month, data) => api.put('/profit/monthly/' + month, data),
};

// A98 Phase 2 — Tracking Analytics
export const brandedTrackingAPI = {
  analytics: (params) => api.get('/branded-tracking/analytics', { params }),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getLabelConfig: () => api.get('/settings/label-config'),
  saveLabelConfig: (data) => api.put('/settings/label-config', data),
  getQcConfig: () => api.get('/settings/qc-config'),
  saveQcConfig: (data) => api.put('/settings/qc-config', data),
  getLabelTemplates: () => api.get('/settings/label-templates'),
  // A83 - Orders page layout: tab order/visibility + column presets (account-level).
  getOrdersView: () => api.get('/settings/orders-view'),
  saveOrdersView: (data) => api.put('/settings/orders-view', data),
  // Live preview HTML for an iframe. `extra` overrides toggles (e.g. { sku: true }) so the picker
  // can preview unsaved changes. Token in query — allowed because the path contains '/label'.
  labelPreviewUrl: (template, extra = {}) => {
    const q = new URLSearchParams({ template, token: localStorage.getItem('oms_dl_token') || '' });
    Object.entries(extra).forEach(([k, v]) => q.set(k, String(v)));
    return `${process.env.REACT_APP_API_URL || '/api'}/settings/label-preview?${q.toString()}`;
  },
  // Phase 2 — custom drag-drop templates
  getCustomTemplates: () => api.get('/settings/custom-templates'),
  getStarterTemplate: () => api.get('/settings/custom-templates/starter'),
  createCustomTemplate: (data) => api.post('/settings/custom-templates', data),
  updateCustomTemplate: (id, data) => api.put(`/settings/custom-templates/${id}`, data),
  deleteCustomTemplate: (id) => api.delete(`/settings/custom-templates/${id}`),
  // WYSIWYG preview of an UNSAVED template (builder). Template JSON base64'd into the query.
  customPreviewUrl: (tpl) => {
    const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(tpl || {}))));
    return `${process.env.REACT_APP_API_URL || '/api'}/settings/label-preview-custom?tpl=${encodeURIComponent(b64)}&token=${localStorage.getItem('oms_dl_token') || ''}`;
  },
};

export const rtoReconcileAPI = {
  config: () => api.get('/rto-reconcile/config'),
  getPending: (params) => api.get('/rto-reconcile/pending', { params }),
  manualReceive: (order_id, location_id) => api.post('/rto-reconcile/manual-receive', { order_id, location_id }),
  receiveReturn: (requestId) => api.post(`/rto-reconcile/return/${requestId}/receive`),
  bulkReceive: (order_ids, location_id) => api.post('/rto-reconcile/bulk-receive', { order_ids, location_id }),
  getBatches: (params) => api.get('/rto-reconcile/batches', { params }),
  getBatch: (id) => api.get(`/rto-reconcile/batches/${id}`),
  confirm: (id, data) => api.post(`/rto-reconcile/batches/${id}/confirm`, data),
  notReceived: (orderId) => api.post(`/rto-reconcile/orders/${orderId}/not-received`),
  confirmReturn: (orderId) => api.post(`/rto-reconcile/orders/${orderId}/confirm-return`),
  prepaidCancel: (orderId) => api.post(`/rto-reconcile/orders/${orderId}/prepaid-cancel`),
  prepaidReship: (orderId) => api.post(`/rto-reconcile/orders/${orderId}/prepaid-reship`),
  prepaidDismiss: (orderId) => api.post(`/rto-reconcile/orders/${orderId}/prepaid-dismiss`),
  startSession: (data) => api.post('/rto-reconcile/scan/session', data),
  validate: (data) => api.post('/rto-reconcile/scan/validate', data),
  getSession: (id) => api.get(`/rto-reconcile/scan/session/${id}`),
  submit: (sessionId) => api.post('/rto-reconcile/scan/submit', { session_id: sessionId }),
  end: (sessionId) => api.post('/rto-reconcile/scan/end', { session_id: sessionId }),
  scanLog: () => api.get('/rto-reconcile/scan/log'),
  getPin: () => api.get('/rto-reconcile/pin'),
  regeneratePin: () => api.post('/rto-reconcile/pin/regenerate'),
  activeSessions: () => api.get('/rto-reconcile/scan/active'),
};

export const returnExchangeAPI = {
  list: (params) => api.get('/return-exchange', { params }),
  create: (data) => api.post('/return-exchange', data),
  get: (id) => api.get(`/return-exchange/${id}`),
  bookPickup: (id, data) => api.post(`/return-exchange/${id}/book-pickup`, data),
  resolve: (id, data) => api.post(`/return-exchange/${id}/resolve`, data),
  refund: (id, data) => api.post(`/return-exchange/${id}/refund`, data),
  updateRefund: (id, data) => api.patch(`/return-exchange/${id}/refund`, data),
  revertRefund: (id) => api.post(`/return-exchange/${id}/refund/revert`),
  updateNote: (id, data) => api.patch(`/return-exchange/${id}/note`, data),
  addNote: (id, text) => api.post(`/return-exchange/${id}/note`, { text }),
  deleteNote: (id, noteId) => api.delete(`/return-exchange/${id}/note/${noteId}`),
  cancel: (id) => api.post(`/return-exchange/${id}/cancel`),
};

// Public PWA scanner (PIN-authed; uses its own bearer token, no user login).
const API_BASE = process.env.REACT_APP_API_URL || '/api';
export const scanAPI = {
  auth: (pin, extra = {}) => axios.post(`${API_BASE}/public/scan/auth`, { pin, ...extra }),
  validate: (token, code) => axios.post(`${API_BASE}/public/scan/validate`, { code }, { headers: { Authorization: `Bearer ${token}` } }),
  session: (token) => axios.get(`${API_BASE}/public/scan/session`, { headers: { Authorization: `Bearer ${token}` } }),
  submit: (token) => axios.post(`${API_BASE}/public/scan/submit`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  end: (token) => axios.post(`${API_BASE}/public/scan/end`, {}, { headers: { Authorization: `Bearer ${token}` } }),
};

export const walletAPI = {
  get: () => api.get('/wallet'),
  rateCard: () => api.get('/wallet/rate-card'),
  passbook: () => api.get('/wallet/passbook'),
  recharge: (amount) => api.post('/wallet/recharge', { amount }),
  rechargeStatus: (id) => api.get(`/wallet/recharge/${id}/status`),
  recharges: () => api.get('/wallet/recharges'),
  invoices: () => api.get('/wallet/invoices'),
  invoicePdfUrl: (id) => `${process.env.REACT_APP_API_URL || '/api'}/wallet/invoices/${id}/pdf?format=pdf&token=${localStorage.getItem('oms_dl_token')}`,
};

export const locationsAPI = {
  getAll: () => api.get('/locations'),
  add: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  lookupPincode: (pin) => api.get(`/locations/pincode/${pin}`),
  delete: (id) => api.delete(`/locations/${id}`),
  addCourier: (id, data) => api.post(`/locations/${id}/couriers`, data),
  removeCourier: (id, courier, account_id) => api.delete(`/locations/${id}/couriers/${courier}`, account_id ? { params: { account_id } } : undefined),
  toggleCourier: (id, courier, account_id) => api.patch(`/locations/${id}/couriers/${courier}/toggle`, account_id ? { account_id } : {}),
  updateCourierPriority: (id, courier, priority, account_id) => api.patch(`/locations/${id}/couriers/${courier}/priority`, { priority, ...(account_id ? { account_id } : {}) }),
  updateCourierRto: (id, courier, rto_location_id, account_id) => api.patch(`/locations/${id}/couriers/${courier}/rto`, { rto_location_id, ...(account_id ? { account_id } : {}) }),
  resyncCourierWarehouse: (id, courier) => api.post(`/locations/${id}/couriers/${courier}/resync-warehouse`),
  getStock: (id) => api.get(`/locations/${id}/stock`),
  regenerateRtoPin: (id) => api.post(`/locations/${id}/rto-pin/regenerate`),
  manifestGroups: (id) => api.get(`/locations/${id}/manifest-groups`),
  manifestUrl: (id, courier, sub) => `${process.env.REACT_APP_API_URL || '/api'}/locations/${id}/manifest?courier=${encodeURIComponent(courier)}&sub=${encodeURIComponent(sub || '')}&token=${localStorage.getItem('oms_dl_token')}`,
};

// A49 — Bulk Actions log: server-side bulk print/ship jobs.
export const bulkActionsAPI = {
  create: (type, orderIds, meta) => api.post('/bulk-actions', { type, order_ids: orderIds, meta }),
  list: () => api.get('/bulk-actions'),
  get: (id) => api.get(`/bulk-actions/${id}`),
};

// A64 — NDR queue. Read-only in Phase 1: there is no act() here because no action has been
// verified against a live courier account yet.
export const ndrAPI = {
  list: () => api.get('/ndr'),
  // A64 Phase 2. `dry_run` builds the courier request and returns it WITHOUT sending — the server
  // enforces the same gate either way, so the UI cannot talk a courier into anything.
  act: (id, body) => api.post(`/ndr/${id}/action`, body),
  dryRun: (id, body) => api.post(`/ndr/${id}/action`, { ...body, dry_run: true }),
  probe: (id) => api.get(`/ndr/${id}/probe`),
};

// A12 — Team & Access (owner-only; the server enforces that, this is just the client surface)
export const teamAPI = {
  list: () => api.get('/team'),
  invite: (payload) => api.post('/team/invites', payload),
  resendInvite: (id) => api.post(`/team/invites/${id}/resend`),
  revokeInvite: (id) => api.delete(`/team/invites/${id}`),
  updateMember: (id, payload) => api.patch(`/team/members/${id}`, payload),
  setMemberDisabled: (id, disabled) => api.post(`/team/members/${id}/disable`, { disabled }),
  removeMember: (id) => api.delete(`/team/members/${id}`),
};

// A88 — System Status. Read-only; courier "Check now" reuses couriersAPI.health.
export const statusAPI = {
  get: () => api.get('/status'),
  mine: (fresh) => api.get('/status/me', fresh ? { params: { fresh: 1 } } : undefined),
  summary: () => api.get('/status/summary'),
};

export default api;
